
import { Component, Provide, Prop, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { Sorters } from "@/data/table";

// Typings
import {
  IOrderTemplatesProviderData,
  IOrderTemplatesProviderMethods
} from "@/models/providers/orderTemplates";
import { IOrderTemplate } from "@/models/orderTemplates";
import { ITableConfig, IFilter, ISorter } from "@/models/tables";

@Component
export default class OrderTemplatesProvider extends Mixins(
  FilteringSortingTableStore
) {
  // Props

  @Prop({ default: () => ({}) })
  public availableFilters!: (vm: OrderTemplatesProvider) => IFilter[];

  @Prop({ default: () => () => [] })
  public availableSorters!: (vm: OrderTemplatesProvider) => ISorter[];

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Variables

  public dataModule: DataModules = DataModules.ORDERS_TEMPLATES;

  public filters = this.orderTemplatesFilters;
  public sorters = this.orderTemplatesSorters;

  public defaults: ITableConfig = {
    page: 1,
    limit: this.limit,
    sort: {
      field: "name",
      direction: Sorters.DESCENDING
    },
    filters: []
  };

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.apiProps = {
      with: "invoice,invoice.products,invoice.client,invoice.client.image"
    };

    this.getData();
  }

  // Methods

  public reloadData() {
    return this.getData({ ignoreStored: true });
  }

  public createOrderTemplate() {
    this.$store.dispatch(
      `data/${DataModules.ORDERS_TEMPLATES}/openSelectOrderModal`,
      {
        events: {
          success: selectedOrderId => {
            this.$store.dispatch(
              `data/${DataModules.ORDERS_TEMPLATES}/openManageOrderTemplateModal`,
              {
                props: {
                  orderId: selectedOrderId
                },
                events: {
                  success: () => {
                    this.$store.dispatch("toast/show", {
                      message: this.$t("_.order_template_successfully_created")
                    });

                    this.reloadData();
                  }
                }
              }
            );
          }
        }
      }
    );
  }

  public updateOrderTemplate(orderTemplate) {
    this.$store.dispatch(
      `data/${DataModules.ORDERS_TEMPLATES}/openManageOrderTemplateModal`,
      {
        props: {
          orderTemplate
        },
        events: {
          success: () => {
            this.$store.dispatch("toast/show", {
              message: this.$t("_.order_template_successfully_update")
            });

            this.reloadData();
          }
        }
      }
    );
  }

  public deleteOrderTemplate(templateId: IOrderTemplate["id"]) {
    this.$store.dispatch(
      `data/${DataModules.ORDERS_TEMPLATES}/deleteOrderTemplate`,
      {
        templateId,
        onSuccess: this.reloadData
      }
    );
  }

  // Computed

  public get canCreate() {
    return this.$userCan("store_invoice_template");
  }

  public get canUpdate() {
    return this.$userCan("update_invoice_template");
  }

  public get canDelete() {
    return this.$userCan("destroy_invoice_template");
  }

  public get orderTemplatesFilters() {
    return this.availableFilters(this);
  }

  public get orderTemplatesSorters() {
    return this.availableSorters(this);
  }

  // Guarding

  public guardFunctionalities = ["list_invoice_templates"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Life-cycle Methods

  public render(h: CreateElement) {
    if (!this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $orderTemplatesData: this.providerData(),
        $orderTemplatesMethods: this.providerMethods
      })
    );
  }

  // Provide

  @Provide("orderTemplatesProviderData")
  public providerData(): IOrderTemplatesProviderData {
    return {
      canCreate: this.canCreate,
      canUpdate: this.canUpdate,
      canDelete: this.canDelete,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      enablePagination: this.enablePagination,
      activeFilters: this.activeFilters,
      filters: this.filters,
      sorters: this.sorters,
      table: this.table
    };
  }

  @Provide("orderTemplatesProviderMethods")
  public get providerMethods(): IOrderTemplatesProviderMethods {
    return {
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      reloadData: this.reloadData,
      createOrderTemplate: this.createOrderTemplate,
      deleteOrderTemplate: this.deleteOrderTemplate,
      updateOrderTemplate: this.updateOrderTemplate
    };
  }
}
